import type { RouteObject } from "react-router-dom";
import { useRoutes } from "react-router-dom";
import { useSeoMeta } from '@unhead/react';
import Navbar from "@/components/Navbar";
import Home from "@/pages/Home";
import EntryLevel from "@/pages/EntryLevel";
import Calculator from "@/pages/Calculator";
import SEO from "@/pages/SEO";
import MineralJobsPage from "@/pages/MineralJobsPage";
import JobBoardSection from "@/components/JobBoard";
import ROICalculator from "@/components/ROICalculator";
import SEODashboard from "@/components/SEODashboard";
import JobAlertSignup from "@/components/JobAlertSignup";
import Auth from "@/pages/Auth";
import Alerts from "@/pages/Alerts";
import Admin from "@/pages/Admin";

// Main layout for the home page
function FIFOPage() {
  useSeoMeta({
    title: 'FIFO Mining Jobs in Australia | MineralJobs',
    description: 'Find FIFO mining jobs in Australia. Fly-In Fly-Out roles across Gold, Copper, Coal and Iron Ore sites. All experience levels welcome.',
    ogTitle: 'FIFO Mining Jobs in Australia | MineralJobs',
    ogUrl: 'https://www.mineraljobs.com/fifo/',
    twitterCard: 'summary_large_image',
  });
  return (
    <main>
      <div className="min-h-screen pt-16" style={{ background: "#111827" }}>
        <div className="max-w-3xl mx-auto px-4 py-12 text-center">
          <h1 className="text-5xl font-black text-white mb-4" style={{ fontFamily: "Outfit, sans-serif" }}>FIFO Mine Jobs</h1>
          <p className="text-gray-400 text-lg mb-8">Fly-In Fly-Out mining positions across Australia. 8/6, 2/1, and swing rosters available.</p>
        </div>
        <JobBoardSection />
      </div>
    </main>
  );
}

// Main layout for the home page
function MainPage() {
  return (
    <>
      <Home />
      <JobBoardSection />
      <JobAlertSignup />
      <ROICalculator />
      <SEODashboard />
      <footer className="border-t border-white/5 py-10 px-4 sm:px-6" style={{ background: "#0f1623" }}>
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-8 mb-8">
            <div>
              <p className="font-bold text-white mb-3" style={{ fontFamily: "Outfit, sans-serif" }}>MineralJobs</p>
              <p className="text-sm text-gray-500">Australia's premier mining job board. FIFO mine jobs, mineral careers, and entry level mining jobs.</p>
            </div>
            {[
              { heading: "Job Categories", links: ["FIFO Mine Jobs", "Entry Level Mining", "Gold Mining", "Iron Ore Jobs", "Copper Careers", "Coal Jobs QLD"] },
              { heading: "Resources", links: ["ROI Calculator", "SEO Dashboard", "Mining Career Guide", "White Card Info", "FIFO Lifestyle Guide"] },
              { heading: "Company", links: ["Employer Hub", "About Us", "Contact", "Privacy Policy"] },
            ].map((col) => (
              <div key={col.heading}>
                <p className="text-xs uppercase tracking-wider text-gray-500 mb-3" style={{ fontFamily: "JetBrains Mono, monospace" }}>{col.heading}</p>
                <ul className="space-y-2">
                  {col.links.map((l) => (
                    <li key={l}><a href="#" className="text-sm text-gray-400 hover:text-orange-400 transition-colors">{l}</a></li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="pt-6 border-t border-white/5 flex flex-col sm:flex-row items-center justify-between gap-2">
            <p className="text-xs text-gray-600" style={{ fontFamily: "JetBrains Mono, monospace" }}>© 2025 MineralJobs Board. All rights reserved.</p>
            <p className="text-xs text-gray-600">Built for miners, by miners 🪨</p>
          </div>
        </div>
      </footer>
    </>
  );
}

export const routes: RouteObject[] = [
  {
    path: "/",
    element: (
      <>
        <Navbar />
        <MainPage />
      </>
    ),
  },
  {
    path: "/entry-level",
    element: (
      <>
        <Navbar />
        <EntryLevel />
      </>
    ),
  },
  {
    path: "/calculator",
    element: (
      <>
        <Navbar />
        <Calculator />
      </>
    ),
  },
  {
    path: "/seo",
    element: (
      <>
        <Navbar />
        <SEO />
      </>
    ),
  },
  {
    path: "/fifo",
    element: (
      <>
        <Navbar />
        <FIFOPage />
      </>
    ),
  },
  {
    path: "/jobs/:mineral",
    element: (
      <>
        <Navbar />
        <MineralJobsPage />
      </>
    ),
  },
  {
    path: "/auth",
    element: (
      <>
        <Navbar />
        <Auth />
      </>
    ),
  },
  {
    path: "/alerts",
    element: (
      <>
        <Navbar />
        <Alerts />
      </>
    ),
  },
  {
    path: "/admin",
    element: (
      <>
        <Navbar />
        <Admin />
      </>
    ),
  },
];

function App() {
  return useRoutes(routes);
}

export default App;
